import React, {
	useState,
	useRef,
	useEffect,
	KeyboardEvent,
	ReactNode,
} from "react";
import { Calendar } from "react-calendar";
import dayjs from "newui/utils/dayjs";
import { Icon } from "assets/Icon";
import { Box, Button, HStack } from "@chakra-ui/react";
import { CommonButtonStyle, DatePickerStyled } from "./style";
import { CalendarViewType, DatePickerProps, VIEW } from "./types";
import { cn } from "utils/helpers";

// extracted from react-calendar/dist/cjs/shared/types
type View = "month" | "year" | "decade" | "century";

const DatePicker: React.FC<DatePickerProps> = ({
	ranged,
	dates,
	minDate,
	className,
	onClickDay,
	onApply,
	onCancel,
	minDateTooltipContent,
}) => {
	const dateValue =
		dates instanceof Date
			? new Date(dates.setHours(0, 0, 0, 0))
			: dayjs().startOf("day").toDate();
	const [calendarView, setView] = useState<CalendarViewType>(VIEW.MONTH);
	const calendarRef = useRef<HTMLDivElement>(null);
	const [date, setDate] = useState<Date | null>(dateValue);
	const [activeDate, setActiveDate] = useState<Date>(dateValue);
	const [showToday, setShowToday] = useState(false);
	const todayDate: Date = dayjs().startOf("day").toDate();
	const minDateOnly = minDate
		? new Date(minDate.setHours(0, 0, 0, 0))
		: minDate;

	const changeDate = (amount: 1 | -1 | 7 | -7) => {
		const newDate = new Date(activeDate);

		if (calendarView === VIEW.MONTH) {
			if (amount === 1 || amount === -1) {
				newDate.setMonth(activeDate.getMonth() + amount);
			} else {
				newDate.setDate(activeDate.getDate() + amount);
			}
		} else if (calendarView === VIEW.YEAR) {
			newDate.setFullYear(activeDate.getFullYear() + amount);
		} else if (calendarView === VIEW.DECADE) {
			newDate.setFullYear(activeDate.getFullYear() + amount * 5);
		}

		safelySetSelectedDate(newDate);
	};

	const safelySetSelectedDate = (date: Date): boolean => {
		if (!minDateOnly || date >= minDateOnly) {
			setActiveDate(date);
			setDate(date);
			return true;
		} else {
			setActiveDate(minDateOnly);
			setDate(minDateOnly);
			return false;
		}
	};

	const goNext = () => {
		changeDate(1);
	};

	const goPrevious = () => {
		changeDate(-1);
	};

	const changeView = () => {
		let newView = calendarView;
		switch (newView) {
			case VIEW.MONTH:
				newView = VIEW.YEAR;
				break;
			case VIEW.YEAR:
				newView = VIEW.DECADE;
				break;
			case VIEW.DECADE:
				newView = VIEW.MONTH;
				break;
		}
		setView(newView);
	};

	const reverseView = (value) => {
		let newView = calendarView;
		switch (newView) {
			case VIEW.YEAR:
				newView = VIEW.MONTH;
				break;
			case VIEW.DECADE:
				newView = VIEW.YEAR;
				break;
		}

		safelySetSelectedDate(value);
		setView(newView);
	};

	const todayHandler = () => {
		setView(VIEW.MONTH);
		if (safelySetSelectedDate(todayDate)) {
			return;
		}

		setShowToday(true);
		setTimeout(() => {
			setShowToday(false);
		}, 1500);
	};

	const onClickDayHandler = (date: Date) => {
		const applyAndClose = onClickDay?.(date) ?? true;

		if (applyAndClose) {
			onApply?.(date, true);
		}
	};

	const onApplyHandler = (close: boolean) => {
		if (!date || (minDateOnly && date < minDateOnly)) {
			return;
		}

		if (date) {
			onApply?.(date, close);
		}
	};

	const handleKeyDown = (e: KeyboardEvent<Document>) => {
		if (!(date instanceof Date)) {
			return;
		}

		switch (e.key) {
			case "ArrowDown":
				changeDate(+7);
				break;
			case "ArrowUp":
				changeDate(-7);
				break;
			case "ArrowLeft":
				changeDate(-1);
				break;
			case "ArrowRight":
				changeDate(+1);
				break;
			case "Enter":
				e.preventDefault();
				onApplyHandler(true);
				break;
			case "Backspace":
				e.preventDefault();
				onCancel();
				break;
		}
	};

	const customTileClass = ({ date, view }): string => {
		if (date?.getTime() !== minDateOnly?.getTime()) {
			return "";
		}

		return "min-date-tile";
	};

	const customTileContent = ({ date, view }): ReactNode => {
		if (!minDateTooltipContent || date.getTime() !== minDateOnly?.getTime()) {
			return;
		}

		return <span data-tooltip={minDateTooltipContent} />;
	};

	useEffect(() => {
		document.addEventListener("keydown", handleKeyDown as any);
		return () => {
			document.removeEventListener("keydown", handleKeyDown as any);
		};
	}, [date]);

	const isTileDisabled = ({ date }) => {
		if (!minDateOnly) {
			return false;
		}

		switch (calendarView) {
			case VIEW.MONTH:
				return minDateOnly && date < minDateOnly;
			case VIEW.YEAR:
				return (
					date.getMonth() < minDateOnly.getMonth() &&
					date.getFullYear() === minDateOnly.getFullYear()
				);
			case VIEW.DECADE:
				return date.getFullYear() < minDateOnly.getFullYear();
		}
	};

	return (
		<DatePickerStyled
			ranged={ranged}
			className={className}
			calendarView={calendarView}
		>
			<HStack
				as="div"
				className="mb-4 flex w-full justify-between font-semibold [&_svg]:cursor-pointer"
			>
				<Icon
					icon="chevron-left"
					onClick={goPrevious}
				/>
				<button onClick={changeView}>
					{dayjs(activeDate).format("MMMM YYYY")}
				</button>
				<Icon
					icon="chevron-right"
					onClick={goNext}
				/>
			</HStack>
			{showToday && (
				<div className="fixed left-1/2 top-4 z-50 -translate-x-1/2 rounded-lg bg-red-200 px-4 py-2 text-red-600 shadow-lg">
					Cannot select today
				</div>
			)}

			{!ranged && (
				<div className="my-5 flex gap-2">
					<div
						className={`pointer-events-none flex ${
							date ? "" : "text-gray-500!"
						} w-full items-center justify-center rounded-md border-2 border-gray-200 p-3`}
					>
						{date ? dayjs(date).format("MMM D, YYYY") : "Select Date"}
					</div>
					<Button
						id="today"
						className={CommonButtonStyle}
						onClick={todayHandler}
					>
						Today
					</Button>
				</div>
			)}

			<div
				ref={calendarRef}
				tabIndex={0}
			>
				<Calendar
					activeStartDate={activeDate}
					formatShortWeekday={(locale, date) =>
						["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"][date.getDay()]
					}
					value={ranged ? dates : date}
					selectRange={ranged}
					minDate={minDateOnly}
					allowPartialRange
					onClickDay={onClickDayHandler}
					tileDisabled={isTileDisabled}
					showWeekNumbers={false}
					showNavigation={false}
					view={calendarView as View}
					onClickMonth={reverseView}
					onClickYear={reverseView}
					tileClassName={customTileClass}
					tileContent={customTileContent}
				/>
			</div>

			{!ranged && (
				<div className="absolute bottom-0 left-0 flex w-full justify-center gap-4 border-t border-gray-200 p-4">
					<Button
						className={CommonButtonStyle}
						onClick={onCancel}
					>
						Cancel
					</Button>
					<Button
						className={cn(
							"bg-royal-blue py-3 !text-white hover:bg-utility-blue-dark-100",
							CommonButtonStyle
						)}
						onClick={() => onApplyHandler(true)}
						data-testid="apply-date"
					>
						Apply
					</Button>
				</div>
			)}
		</DatePickerStyled>
	);
};

export default DatePicker;
